/* eslint-disable arrow-body-style */
import {inject} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {catchError, EMPTY, exhaustMap, map, of, switchMap} from "rxjs";
import {AssetApiService} from "../../generated-sources/api";
import {GptApiService} from "../../../shared/generated-sources/api/gpt";
import {AssetActions, ComparableCompanyActions} from "../asset.actions";
import {
    selectComparableCompaniesData,
    selectGeneralPartnerValuation,
    selectSelectedAsset,
    selectSelectedAssetId,
    selectAssetFinancialHistoryByPeriods
} from "./asset.selectors";
import {selectSelectedSourceDataset} from "../../../import/store/import.selectors";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {concatLatestFrom} from "@ngrx/operators";
import {ComparableSuggestRequest} from "../../../shared/generated-sources/api/gpt";

export const loadComparableCompanies = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetApiService = inject(AssetApiService)) => {
        return actions$.pipe(
            ofType(ComparableCompanyActions.load),
            concatLatestFrom(() => store$.select(selectGeneralPartnerValuation)),
            exhaustMap(([action, gpValuation]) => {
                const fundInvestmentId = action.fundInvestmentId ?? gpValuation?.data?.id;
                return assetApiService.getComparableCompanies(action.assetId, fundInvestmentId).pipe(
                    map((comparableCompanySet) => ComparableCompanyActions.loadSuccess({comparableCompanySet})),
                    catchError((errorResponse: any) => of(ComparableCompanyActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

const selectFinancialHistory = selectAssetFinancialHistoryByPeriods(["FULL_FISCAL_YEAR", "LAST_TWELVE_MONTHS"] as const);

export const suggestComparableCompanies = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), gptApiService = inject(GptApiService)) => {
        return actions$.pipe(
            ofType(ComparableCompanyActions.suggest),
            concatLatestFrom(() => [
                store$.select(selectSelectedAsset),
                store$.select(selectFinancialHistory),
                store$.select(selectComparableCompaniesData)
            ]),
            exhaustMap(([action, selectedAsset, financialHistory, existingData]) => {
                const asset = selectedAsset.data;

                // Find latest non-forecast financial record for revenue/EBITDA context
                const latestRecord = (financialHistory?.data ?? [])
                    .filter(r => !r.forecast)
                    .sort((a, b) => (b.period?.keyDate ?? "").localeCompare(a.period?.keyDate ?? ""))
                    [0];

                const existingTickers = (existingData?.companies ?? [])
                    .map(c => c.ticker)
                    .filter(t => !!t);

                const request: ComparableSuggestRequest = {
                    companyName: asset.name?.text ?? "",
                    industry: asset.gic?.code || undefined,
                    description: asset.description?.text || undefined,
                    country: asset.country?.code || undefined,
                    websiteUrl: asset.url?.text || undefined,
                    revenue: latestRecord?.revenue?.amount ?? undefined,
                    ebitda: latestRecord?.ebitda?.amount ?? undefined,
                    existingTickers: existingTickers.length > 0 ? existingTickers : undefined
                };
                const assetId = asset.id;
                return gptApiService.suggestComparableCompanies(request).pipe(
                    switchMap((companies) => {
                        const mapped = companies.map(c => ({
                            ...c,
                            companyName: c.companyName || c.ticker,
                            source: c.source ?? "gpt"
                        }));
                        const allCompanies = [...(existingData?.companies ?? []), ...mapped];
                        // "Good" comps are linked to a real Google Finance ticker (profileUrl set).
                        // Redirect the user to enrich the asset when fewer than 3 exist in total.
                        const goodCompanies = allCompanies.filter(c => !!c.profileUrl).length;
                        return [
                            ComparableCompanyActions.suggestSuccess({companies: allCompanies, lowConfidence: goodCompanies < 3}),
                            ComparableCompanyActions.save({assetId: assetId!, companies: allCompanies})
                        ];
                    }),
                    catchError((errorResponse: any) => of(ComparableCompanyActions.suggestError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const refreshMarketData = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), gptApiService = inject(GptApiService)) => {
        return actions$.pipe(
            ofType(ComparableCompanyActions.refresh),
            concatLatestFrom(() => store$.select(selectSelectedAssetId)),
            exhaustMap(([action, assetId]) =>
                gptApiService.fetchMarketData({tickers: action.tickers}).pipe(
                    switchMap((companies) => {
                        // Merge refreshed data with existing data to preserve company names and other fields
                        const mapped = companies.map(c => {
                            const existing = action.existingCompanies?.find(e => e.ticker === c.ticker);
                            return {
                                ...existing,
                                ...c,
                                companyName: c.companyName && c.companyName !== c.ticker ? c.companyName : (existing?.companyName ?? c.companyName ?? c.ticker),
                                analystNote: existing?.analystNote ?? c.analystNote,
                                source: c.source ?? existing?.source ?? "market-data"
                            };
                        });
                        return [
                            ComparableCompanyActions.refreshSuccess({companies: mapped}),
                            ComparableCompanyActions.save({assetId: assetId!, companies: mapped})
                        ];
                    }),
                    catchError((errorResponse: any) => of(ComparableCompanyActions.refreshError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const saveComparableCompanies = createEffect(
    (actions$ = inject(Actions), assetApiService = inject(AssetApiService)) => {
        return actions$.pipe(
            ofType(ComparableCompanyActions.save),
            exhaustMap((action) =>
                assetApiService.saveComparableCompanies(action.assetId, action.companies).pipe(
                    switchMap((companies) => [
                        ComparableCompanyActions.saveSuccess({companies}),
                        ComparableCompanyActions.load({assetId: action.assetId})
                    ]),
                    catchError((errorResponse: any) => of(ComparableCompanyActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const removeComparableCompany = createEffect(
    (actions$ = inject(Actions), assetApiService = inject(AssetApiService)) => {
        return actions$.pipe(
            ofType(ComparableCompanyActions.remove),
            exhaustMap((action) =>
                assetApiService.deleteComparableCompany(action.assetId, action.compId).pipe(
                    switchMap(() => [
                        ComparableCompanyActions.removeSuccess(),
                        ComparableCompanyActions.load({assetId: action.assetId})
                    ]),
                    catchError((errorResponse: any) => of(ComparableCompanyActions.removeError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const updateComparableCompanyNote = createEffect(
    (actions$ = inject(Actions), assetApiService = inject(AssetApiService)) => {
        return actions$.pipe(
            ofType(ComparableCompanyActions.updateNote),
            exhaustMap((action) =>
                assetApiService.updateComparableCompanyNote(action.assetId, action.compId, {note: action.note}).pipe(
                    switchMap(() => [
                        ComparableCompanyActions.updateNoteSuccess(),
                        ComparableCompanyActions.load({assetId: action.assetId})
                    ]),
                    catchError((errorResponse: any) => of(ComparableCompanyActions.updateNoteError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadComparableCompaniesOnAssetDataSuccess = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetActions.loadAssetDataSuccess),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            switchMap(([action, dataset]) => {
                if (dataset === "master") {
                    return of(ComparableCompanyActions.load({
                        assetId: action.assetData.asset.id!,
                        fundInvestmentId: action.assetData.fundInvestment?.id
                    }));
                }
                return EMPTY;
            })
        );
    },
    {functional: true}
);
